import React from 'react';
import { useSelector } from 'react-redux';
import WarningIcon from '@material-ui/icons/Warning';
import ReportIcon from '@material-ui/icons/Report';
import styled from 'styled-components';
import { RootState } from '../../redux/root';
import { dispatch } from '../../redux/store';
import { setDeleteError, setShowDeleteMessage, setShowDeleteWarning } from '../../redux/slices/inspectionsSlice';
import { ModalDefaultDraggable } from '../../molecules';
import { ButtonMain } from '../../atoms';

const InspectionDeletePopup = ({
  selectedRecord,
  handleDeleteInspection,
  showClearWarning = false,
  onConfirmClear,
  onCancelClear,
}: Props) => {
  const { showDeleteMessage, deleteError, showDeleteWarning } = useSelector((s: RootState) => s.inspections);
  const handleCloseWarning = () => {
    dispatch(setShowDeleteWarning(false));
    dispatch(setDeleteError(false));
  };
  const handleClose = () => {
    dispatch(setShowDeleteMessage(false));
    dispatch(setDeleteError(false));
  };

  // Only the fields that actually hold something get listed, so the message never
  // names a field the user can see is already empty.
  const populatedFields = [
    selectedRecord?.inspector ? `Inspector (${selectedRecord.inspector})` : null,
    selectedRecord?.inspectionDate ? `Inspection date (${selectedRecord.inspectionDate})` : null,
    selectedRecord?.followUp ? `Follow-up date (${selectedRecord.followUp})` : null,
  ].filter(Boolean);

  return (
    <>
      {showClearWarning && (
      <ModalDefaultDraggable
        title="This inspection still has scheduling information"
        onClose={onCancelClear}
      >
        <>
          <CenteredIcon>
            <WarningIcon />
          </CenteredIcon>
          <ModalMessage>
            {`Inspection ID: ${selectedRecord?.id} - ${selectedRecord?.inspectionType}`}
          </ModalMessage>
          <ClearList>
            The following will be cleared before the inspection is deleted:
            <ul>
              {populatedFields.map(field => (
                <li key={field}>{field}</li>
              ))}
            </ul>
          </ClearList>

          <ButtonContainer>
            <ButtonMain color="secondary" onClick={onConfirmClear}>
              Clear and continue
            </ButtonMain>
            <ButtonMain color="default" onClick={onCancelClear}>
              Cancel
            </ButtonMain>
          </ButtonContainer>
        </>
      </ModalDefaultDraggable>
      )}
      {showDeleteMessage && <ModalDefaultDraggable title="Deletion Not Allowed" onClose={handleClose}>
        <>
          <ModalMessage>This inspection cannot be deleted unless it is in Not Scheduled status.</ModalMessage>
          <Icon isWarning={false}>
            <WarningIcon />
          </Icon>
          <br />

          <ButtonContainer>
            <ButtonMain color="default" onClick={handleClose}>
              close
            </ButtonMain>
          </ButtonContainer>
        </>
                            </ModalDefaultDraggable>}
      {showDeleteWarning && (
      <ModalDefaultDraggable
        title="Are you sure you want to delete?"
        onClose={handleCloseWarning}
      >
        <>
          <ModalMessage>
            {`Inspection ID: ${selectedRecord.id} - ${selectedRecord.inspectionType}`}
          </ModalMessage>
          <Icon isWarning>
            <ReportIcon />
          </Icon>
          <br />

          <ButtonContainer>
            <ButtonMain color="secondary" onClick={handleDeleteInspection}>
              Yes, delete
            </ButtonMain>
            <ButtonMain color="default" onClick={handleCloseWarning}>
              No
            </ButtonMain>
          </ButtonContainer>
        </>
      </ModalDefaultDraggable>
        )}
    </>
  );
};

type Props = {
  selectedRecord: any;
  handleDeleteInspection: () => void;
  showClearWarning?: boolean;
  onConfirmClear?: () => void;
  onCancelClear?: () => void;
};

export default InspectionDeletePopup;

const CenteredIcon = styled.div`
  display: flex;
  justify-content: center;
  color: var(--warning-500, orange);

  svg {
    font-size: 40px;
  }
`;

const ClearList = styled.div`
  margin: 16px 0 24px;
  font-size: 0.875rem;
  color: var(--mainText);

  ul {
    margin: 8px 0 0;
    padding-left: 20px;
  }

  li {
    margin-bottom: 4px;
  }
`;

const Icon = styled.div<{isWarning: boolean}>`
  position: relative;
  left: 135px;
  top: -78px;
  color: ${({ isWarning }) => isWarning ? 'red' : 'orange'};
  width: fit-content;
  svg {
    scale: 1.5;
  }
`;

const ModalMessage = styled.div`
  width: 100%;
  text-align: center;
  font-weight: bold;
`;

const ButtonContainer = styled.div`
  display: flex;
  text-align: center;
  align-items: center;
  justify-content: center;

  > .MuiButton-root {
    margin: 0 20px 0 0;

    &:last-child {
      margin: 0;
    }
  }
`;
